<template>
  <div class="rb-ob-form rb-match-page rb-book-page rb-pay-page">
    <header>
      <h1 class="rb-ob-title">{{ $t('intake.pay02Title') }}</h1>
    </header>

    <div class="rb-pay-processing" role="status" aria-live="polite">
      <div class="rb-pay-orb" aria-hidden="true"></div>
      <p class="rb-match-outcome-body">{{ $t('intake.pay02Body') }}</p>
    </div>

    <ul class="rb-pay-summary" :aria-label="$t('intake.pay02SummaryAria')">
      <li>
        <span class="rb-pay-summary-k">{{ $t('intake.pay02Ref') }}</span>
        <span class="rb-pay-summary-v"><bdi>{{ toFaDigits(bookingCode) }}</bdi></span>
      </li>
      <li>
        <span class="rb-pay-summary-k">{{ $t('intake.holdSummaryAmount') }}</span>
        <span class="rb-pay-summary-v">
          {{ displayAmount }}
        </span>
      </li>
      <li>
        <span class="rb-pay-summary-k">{{ $t('intake.pay02BookingStatus') }}</span>
        <span class="rb-pay-summary-v">{{ statusLabel }}</span>
      </li>
    </ul>

    <div class="rb-ob-actions">
      <button class="rb-ob-submit" type="button" :disabled="resolving" @click="checkStatus">
        {{ $t('intake.pay02Check') }}
      </button>
      <p class="rb-ob-status" role="status" aria-live="polite">{{ statusMessage }}</p>
      <div class="rb-ob-trust">
        <p>{{ $t('intake.trustPath') }}</p>
        <p>{{ $t('authUi.familyNote') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  loadIntake,
  saveIntake,
  toFaDigits,
} from '@/intake/intakeState'
import {formatMoneyForDisplay} from '@/intake/formatMoney'
import {
  getBooking,
  isMatchmakingV2ApiEnabled,
  isMockCheckoutForced,
  paymentReturn,
} from '@/intake/sandboxMatchApi'
import {
  loadMatchSession,
  saveMatchSession,
} from '@/intake/sandboxMatchSession'
import {bookingReturnIdentifiers, routeForBookingState} from '@/intake/bookingPayRouting'

const MAX_POLLS = 48 // ~2 minutes at 2.5s

export default {
  name: 'MatchPayProcessingStep',
  data() {
    const draft = loadIntake()
    const session = loadMatchSession() || {}
    const identifiers = bookingReturnIdentifiers(this.$route.query || {}, draft, session)
    return {
      bookingCode: '',
      amount: session.acceptedAmount != null ? session.acceptedAmount : null,
      currency: session.acceptedCurrency || null,
      resolving: false,
      pollTimer: null,
      pollCount: 0,
      bookingId: identifiers.booking_id || '',
      returnIdentifiers: identifiers,
      disposed: false,
      bookingState: '',
      statusMessage: '',
    }
  },
  computed: {
    useLiveBooking() {
      return isMatchmakingV2ApiEnabled() && !isMockCheckoutForced()
    },
    displayAmount() {
      if (this.amount == null) {
        return '-'
      }
      const money = formatMoneyForDisplay(this.amount, this.currency)
      if (money.isToman || money.unit === 'raw') {
        return money.text + ' ' + this.$t('intake.toman')
      }
      if (money.currency) {
        return money.text + ' ' + money.currency
      }
      return money.text
    },
    statusLabel() {
      if (this.bookingState === 'CONFIRMED') {
        return 'UNKNOWN'
      }
      if (this.bookingState) {
        return this.bookingState
      }
      return this.$t('intake.pay02BookingActive')
    },
  },
  created() {
    const draft = loadIntake()
    if (!draft.therapistId && !this.bookingId && !this.returnIdentifiers.payment_id && !this.returnIdentifiers.cart_id) {
      this.$router.replace({name: 'intakeCheckout'})
      return
    }
    if (this.useLiveBooking) {
      this.bootstrapLive()
      return
    }
    this.statusMessage = 'PAYMENT_STATUS_RUNTIME_UNAVAILABLE'
  },
  beforeDestroy() {
    this.disposed = true
    this.stopPolling()
  },
  methods: {
    toFaDigits,
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    async bootstrapLive() {
      if (this.resolving || this.disposed) return
      const identifiers = this.returnIdentifiers
      if (identifiers.payment_id || identifiers.cart_id) {
        this.resolving = true
        let resolved = false
        try {
          const data = await paymentReturn(identifiers)
          if (this.disposed) return
          this.applyBookingData(data)
          resolved = true
        } catch (_) {
          if (this.disposed) return
          // Do not replace a failed identified callback with an unrelated
          // current booking (or a stale draft). The refresh button retries it.
          this.statusMessage = this.$route.params.lang === 'en'
            ? 'Payment status could not be checked. Retry this payment; do not pay again if your account was charged.'
            : 'وضعیت این پرداخت دریافت نشد. دوباره وضعیت را بررسی کن؛ اگر مبلغ کسر شده، دوباره پرداخت نکن.'
        } finally {
          this.resolving = false
        }
        if (resolved && !this.disposed) this.startPolling()
        return
      }
      this.startPolling()
    },
    startPolling() {
      this.stopPolling()
      if (!this.bookingId || this.disposed) return
      this.pollCount = 0
      this.pollBooking()
      this.pollTimer = setInterval(() => this.pollBooking(), 2500)
    },
    applyBookingData(data) {
      if (!data) {
        return
      }
      this.bookingState = data.state || data.client_status || ''
      if (data.booking_id) {
        this.bookingId = data.booking_id
      }
      if (data.booking_code) {
        this.bookingCode = data.booking_code
      }
      if (data.accepted_amount != null) {
        this.amount = data.accepted_amount
      }
      if (data.accepted_currency) {
        this.currency = data.accepted_currency
      }
      saveIntake({
        bookingId: this.bookingId,
        bookingCode: this.bookingCode || undefined,
      })
      saveMatchSession({
        bookingId: this.bookingId,
        bookingCode: this.bookingCode,
        acceptedAmount: this.amount,
        acceptedCurrency: this.currency,
      })
    },
    checkStatus() {
      if (this.useLiveBooking) {
        if (this.returnIdentifiers.payment_id || this.returnIdentifiers.cart_id) return this.bootstrapLive()
        this.pollCount = 0
        this.pollBooking(true)
        return
      }
      this.statusMessage = 'PAYMENT_STATUS_RUNTIME_UNAVAILABLE'
    },
    routeForState(state) {
      const mapped = routeForBookingState(state)
      if (!mapped) {
        return null
      }
      const lang = this.$route.params.lang || 'fa'
      saveIntake({paymentOutcome: mapped.paymentOutcome, bookingCode: this.bookingCode})
      return {name: mapped.name, params: {lang}, query: this.bookingId ? {bookingId: this.bookingId} : {}}
    },
    pollBooking(forceRoute) {
      if (this.resolving || this.disposed) {
        return
      }
      if (!this.bookingId) {
        if (forceRoute) {
          this.statusMessage = this.$t('intake.formIncomplete')
        }
        return
      }
      this.pollCount += 1
      if (this.pollCount > MAX_POLLS) {
        this.stopPolling()
        this.statusMessage = this.$t('intake.pay02Body')
        return
      }
      this.resolving = true
      getBooking(this.bookingId).then((data) => {
        if (this.disposed) return
        this.applyBookingData(data)
        const dest = this.routeForState(data.state)
        if (dest) {
          this.stopPolling()
          this.$router.replace(dest)
          return
        }
        if (forceRoute) {
          this.statusMessage = this.bookingState || ''
        }
      }).catch(() => {
        if (forceRoute) {
          this.statusMessage = this.$t('intake.formIncomplete')
        }
      }).finally(() => {
        this.resolving = false
      })
    },
  },
}
</script>
